import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, HttpError } from '../api/client';
import type { GenerateQuality } from '../api/types';
import type { Slide } from '../lib/deck-model';
import { navigate } from '../lib/router-state';
import { setPageTitle } from '../lib/page-title';
import { renderDeck } from '../lib/render-deck';
import {
  BLOCK_TYPES,
  SLIDE_VARIANTS,
  type Block,
  type BlockType,
  type DeckModel,
  type SlideVariant,
} from '../lib/deck-model';
import {
  emptyDeck,
  updateMeta,
  addSlide,
  removeSlide,
  moveSlide,
  setSlideVariant,
  addBlock,
  removeBlock,
  moveBlock,
  updateBlock,
  insertSlideTemplate,
} from '../lib/deck-outline';
import {
  buildRefineRequest,
  conversationToThread,
  threadToConversation,
  type ThreadTurn,
} from '../lib/refine-request';
import { getBuiltinTemplates, type SlideTemplate } from '../lib/slide-template';

function showToast(message: string) {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = message;
  document.body.appendChild(el);
  window.setTimeout(() => el.remove(), 2400);
}

/** A single slide's fit measurement, reported by <deck-stage> via postMessage
 *  (`deck:fit-report`). `scale` is the auto-fit scale (1 = fits at full size;
 *  lower = shrunk); `overflow` means it still clips at the fit floor. */
interface FitSlide {
  index: number;
  label: string;
  scale: number;
  overflow: boolean;
}

/** A slide whose auto-fit scale is below this is "overpacked" — small enough to
 *  hurt readability and worth an automatic revise. Mirrors eval.OverpackThreshold. */
const OVERPACK_THRESHOLD = 0.75;

/** Promise-based delay. */
const delay = (ms: number): Promise<void> => new Promise((r) => window.setTimeout(r, ms));

/** The two authoring modes the page offers. */
type Mode = 'generate' | 'build';

/** Quality options for the AI generate control. */
const QUALITY_CHOICES: ReadonlyArray<{ value: GenerateQuality; label: string }> = [
  { value: 'fast', label: 'Fast' },
  { value: 'standard', label: 'Standard' },
  { value: 'deep', label: 'Deep' },
];

/** Image-generation style presets (BUILD mode). Wire values mirror the server's
 *  style enum 1:1; the label is the human-readable form shown in the select. */
const IMAGE_STYLE_CHOICES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'editorial-photo', label: 'Editorial photo' },
  { value: 'abstract-gradient', label: 'Abstract gradient' },
  { value: 'line-illustration', label: 'Line illustration' },
  { value: '3d-render', label: '3D render' },
  { value: 'duotone', label: 'Duotone' },
];

/** Image look/theme presets — a property of the generated image, NOT the deck's
 *  display theme. Kept deliberately separate from the deck theme toggle. */
const IMAGE_THEME_CHOICES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'coral', label: 'Coral' },
  { value: 'navy', label: 'Navy' },
];

/** Aspect-ratio presets. Wire values mirror the server's aspect enum. */
const IMAGE_ASPECT_CHOICES: ReadonlyArray<{ value: string; label: string }> = [
  { value: '16:9', label: '16:9' },
  { value: '4:3', label: '4:3' },
  { value: '1:1', label: '1:1' },
];

/**
 * Deck CREATE surface. Two modes share a Title field, a live preview iframe,
 * and a Save action:
 *
 *   - GENERATE: prompt + controls -> POST /api/decks/generate -> DeckModel ->
 *     compile via renderDeck() -> live preview + seed the editable outline.
 *   - BUILD: an editable outline bound to a DeckModel in component state.
 *     Every edit recompiles (debounced) and updates the preview.
 *
 * Both modes hold the SAME `model` in state, so a generated deck drops straight
 * into the manual builder for refinement, and Save always compiles whatever is
 * currently in `model`.
 *
 * Rendered into light DOM so global.css (form primitives, preview pane) cascades
 * without re-importing tokens — same pattern as the other pages. The preview
 * iframe reuses the deck-viewer-page sandbox attributes exactly.
 */
@customElement('create-page')
export class CreatePage extends LitElement {
  @state() private mode: Mode = 'generate';

  /** Resume target. Bound by the `/new/:id` route (`deck-id=…`); empty on the
   *  fresh `/new` route. When present, firstUpdated() rehydrates the draft. */
  @property({ attribute: 'deck-id' }) deckIdParam = '';

  /** The server-assigned id of the deck this page is editing, set the moment an
   *  instant draft is created on first generation (or on resume). Null until a
   *  deck exists; gates persistDraft() and the Save/Keep promotion. */
  @state() private deckId: string | null = null;

  /** Lifecycle state of the deck this page is editing. `draft` while it is an
   *  instant-saved work-in-progress; `saved` once the owner keeps it. Null when
   *  no deck exists yet (pure from-scratch build before any save). */
  @state() private deckStatus: 'draft' | 'saved' | null = null;

  /** The authoritative model both modes edit. Seeded with an empty deck so the
   *  builder + preview are never blank. */
  @state() private model: DeckModel = emptyDeck();

  /** Compiled HTML for the preview iframe — recomputed (debounced) on edits. */
  @state() private compiledHtml = '';

  // Generate-mode controls.
  @state() private prompt = '';
  @state() private slideCount = 8;
  @state() private tone = '';
  @state() private quality: GenerateQuality = 'standard';
  @state() private generating = false;

  // ── Generation v2 UX ──
  /** When true, render the full-viewport draft-preview overlay. The page stays
   *  in generation mode the whole time — the overlay is NOT the deck viewer. */
  @state() private previewMaximised = false;

  /** The visible refine conversation: the user's prompts + short assistant acks.
   *  Empty until the first generation; once populated the generate controls
   *  collapse into a chat composer + "Start over". */
  @state() private thread: ThreadTurn[] = [];

  /** The follow-on refine instruction being composed. */
  @state() private refineInput = '';

  // Save state.
  @state() private saving = false;
  @state() private error: string | null = null;

  // Advanced JSON editor.
  @state() private jsonOpen = false;
  @state() private jsonText = '';
  @state() private jsonError: string | null = null;

  // ── Insert-from-library picker (BUILD mode) ──
  /** When true, render the reusable-slide picker modal. Manual builder only. */
  @state() private libraryOpen = false;

  // ── AI image generation (BUILD mode) ──
  /** What the image should depict. Required before a request is sent. */
  @state() private imageSubject = '';
  /** Image style preset — wire value from IMAGE_STYLE_CHOICES. */
  @state() private imageStyle = 'editorial-photo';
  /** Image look/theme preset — independent of the deck's display theme. */
  @state() private imageTheme = 'light';
  /** Image aspect ratio — wire value from IMAGE_ASPECT_CHOICES. */
  @state() private imageAspect = '16:9';
  /** True while a generate-image request is in flight (disables the button). */
  @state() private generatingImage = false;

  private recompileTimer?: number;

  /** Single debounce timer for draft auto-save. Coalesces rapid edits into one
   *  persistDraft() call; flushed on disconnectedCallback. */
  private persistTimer?: number;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    setPageTitle('New deck', 'Start a new Folio deck.');
    window.addEventListener('themechange', this.onThemeChange);
    window.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);
    this.compileNow();
  }

  /** Resume runs in firstUpdated so the `deck-id` attribute set by the router
   *  has been reflected onto `deckIdParam` first. A bare /new (no id) is a
   *  no-op — the page stays a fresh start. */
  firstUpdated() {
    const id = this.deckIdParam.trim() || this.deckIdFromPath();
    if (id) void this.resumeDraft(id);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('themechange', this.onThemeChange);
    window.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    if (this.recompileTimer) window.clearTimeout(this.recompileTimer);
    // Flush a pending draft auto-save so an in-flight debounce is not lost when
    // the page unmounts (e.g. navigating to the saved viewer).
    if (this.persistTimer) {
      window.clearTimeout(this.persistTimer);
      this.persistTimer = undefined;
      void this.persistDraft();
    }
  }

  /** Parse a deck id out of a /new/<id> pathname as a fallback for when the
   *  router has not (yet) bound the `deck-id` attribute. Returns '' for the
   *  bare /new route. */
  private deckIdFromPath(): string {
    const m = window.location.pathname.match(/^\/new\/([^/]+)\/?$/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  /** Rehydrate an instant-saved draft on a hard refresh / direct link. Restores
   *  the editable model, the refine thread, and the lifecycle status, then drops
   *  the user straight into the builder. A missing deck (404 or any load error)
   *  falls back to a fresh /new so a stale link never strands the page. */
  private async resumeDraft(id: string) {
    try {
      const deck = await api.getDeck(id);
      this.model = deck.model ?? this.compileFallbackModel(deck.html);
      this.deckId = deck.id;
      this.deckStatus = deck.status ?? 'saved';
      this.thread = conversationToThread(deck.conversation ?? []);
      this.mode = 'build';
      if (this.jsonOpen) this.jsonText = JSON.stringify(this.model, null, 2);
      this.compileNow();
    } catch (err) {
      if (err instanceof HttpError && err.status === 404) {
        showToast('That draft no longer exists — starting fresh.');
      } else {
        showToast('Could not load that draft — starting fresh.');
      }
      this.deckId = null;
      this.deckStatus = null;
      this.deckIdParam = '';
      navigate('/new');
    }
  }

  /** A draft with no stored `model` (legacy / html-only) still needs SOMETHING
   *  editable. We cannot decompile html back to a model, so seed an empty deck
   *  carrying the html-derived title via meta — the live preview re-renders from
   *  the (empty) model, but the user keeps editing rather than seeing a crash.
   *  In practice every instant draft persists its model, so this is defensive. */
  private compileFallbackModel(_html: string): DeckModel {
    return emptyDeck();
  }

  /** Re-fit every open library thumbnail when the viewport changes. The inline
   *  + overlay previews are layout-fitted by CSS (width/height 100%), so only
   *  the scale-transformed thumbnails need re-fitting. */
  private onResize = () => {
    this.querySelectorAll<HTMLIFrameElement>('iframe.tpl-thumb-frame').forEach((f) =>
      this.fitThumb(f),
    );
  };

  /** Esc closes the maximised draft-preview overlay, or the library picker. */
  private onKeydown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    if (this.libraryOpen) {
      this.libraryOpen = false;
      return;
    }
    if (this.previewMaximised) {
      this.previewMaximised = false;
    }
  };

  // ── Preview theming (mirrors deck-viewer-page) ──────────────────────────────
  private onThemeChange = (e: Event) => {
    const detail = (e as CustomEvent<{ theme: 'light' | 'dark' }>).detail;
    this.broadcastTheme(detail?.theme ?? this.currentTheme());
  };

  private currentTheme(): 'light' | 'dark' {
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  }

  /** Post the theme to a single preview iframe's contentWindow. */
  private postThemeToFrame(frame: HTMLIFrameElement | null | undefined, theme: 'light' | 'dark') {
    try {
      frame?.contentWindow?.postMessage({ type: 'deck:theme', theme }, '*');
    } catch {
      /* defensive */
    }
  }

  /** Theme every live preview iframe (the inline pane, the overlay when open,
   *  and the library picker's per-template thumbnails when open). They all
   *  render deck-system HTML and so must stay themed together. */
  private broadcastTheme(theme: 'light' | 'dark') {
    const frames = this.querySelectorAll<HTMLIFrameElement>(
      'iframe.preview, iframe.preview-overlay-frame, iframe.tpl-thumb-frame',
    );
    frames.forEach((f) => this.postThemeToFrame(f, theme));
  }

  /** A preview iframe finished loading — theme THAT iframe so whichever one
   *  just (re)loaded initialises in the right mode (no flash). Shared verbatim
   *  by the inline pane and the maximised overlay. */
  private onIframeLoad = (e: Event) => {
    this.postThemeToFrame(e.target as HTMLIFrameElement, this.currentTheme());
  };

  // ── Compilation ─────────────────────────────────────────────────────────────
  /** Compile immediately (no debounce). Used on mount + after generate/import. */
  private compileNow() {
    try {
      this.compiledHtml = renderDeck(this.model);
    } catch (err) {
      // renderDeck is deterministic and total over a valid model, but a hand-
      // edited JSON model could be malformed — surface rather than crash.
      this.error = err instanceof Error ? err.message : 'Failed to compile preview';
    }
  }

  // ── Visual self-check (auto-fit) ──────────────────────────────────────────
  /** Ask the live preview's <deck-stage> for its per-slide fit report and
   *  resolve with it. Posts `deck:report-fit` to the inline preview iframe; the
   *  stage re-fits and posts back `deck:fit-report`. Resolves null on timeout
   *  (treated as "no issues"). */
  private measureFit(timeoutMs = 2500): Promise<FitSlide[] | null> {
    const iframe = this.querySelector<HTMLIFrameElement>('iframe.preview');
    return new Promise((resolve) => {
      let done = false;
      const finish = (v: FitSlide[] | null) => {
        if (done) return;
        done = true;
        window.removeEventListener('message', onMsg);
        resolve(v);
      };
      const onMsg = (e: MessageEvent) => {
        const d = e.data as { type?: string; slides?: FitSlide[] };
        if (d && d.type === 'deck:fit-report' && Array.isArray(d.slides)) finish(d.slides);
      };
      window.addEventListener('message', onMsg);
      try {
        iframe?.contentWindow?.postMessage({ type: 'deck:report-fit' }, '*');
      } catch {
        /* iframe not ready / cross-origin — fall through to timeout */
      }
      window.setTimeout(() => finish(null), timeoutMs);
    });
  }

  /** After a fresh generation, measure the rendered deck and auto-revise any
   *  overpacked slides (real auto-fit scale below the floor, or overflowing).
   *  Up to `maxRounds` silent refines; stops when everything fits or the budget
   *  is spent (the auto-fit floor guarantees nothing is ever broken, just
   *  possibly small). Synthetic refines are NOT added to the conversation. */
  private async autoFitRevise(maxRounds = 2) {
    for (let round = 0; round < maxRounds; round++) {
      // Let the preview iframe (re)load + the stage boot/fit before measuring.
      await delay(500);
      const report = await this.measureFit();
      if (!report || report.length === 0) return;
      const bad = report.filter((s) => s.overflow || s.scale < OVERPACK_THRESHOLD);
      if (bad.length === 0) return; // everything fits — done
      const names = bad.map((s) => `"${s.label}"`).join(', ');
      const instruction =
        `These slides are overpacked and shrink below a readable size: ${names}. ` +
        `Reduce ONLY those slides so each fits at full size — use fewer or shorter ` +
        `blocks, or split a slide into two. Keep every other slide identical, and ` +
        `return the COMPLETE updated deck.`;
      try {
        const model = await api.generateDeck({
          prompt: instruction,
          currentModel: this.model,
          quality: this.quality,
        });
        this.model = model;
        if (this.jsonOpen) this.jsonText = JSON.stringify(this.model, null, 2);
        this.compileNow();
      } catch {
        return; // refine failed — keep the best deck we have
      }
    }
  }

  /** Debounced recompile — called after every outline edit so rapid typing does
   *  not thrash the iframe. */
  private scheduleCompile() {
    if (this.recompileTimer) window.clearTimeout(this.recompileTimer);
    this.recompileTimer = window.setTimeout(() => this.compileNow(), 200);
  }

  /** Apply a model mutation, refresh the JSON mirror if open, and recompile. */
  private apply(next: DeckModel) {
    this.model = next;
    if (this.jsonOpen) this.jsonText = JSON.stringify(this.model, null, 2);
    this.scheduleCompile();
    // Outline-edit mutation path: keep the draft in sync once it exists.
    this.schedulePersist();
  }

  // ── Draft persistence helpers ────────────────────────────────────────────
  /** The visible refine thread mapped to the persisted conversation shape.
   *  Delegates to the pure helper (unit-tested in refine-request.test.ts). */
  private threadToConversation(): { role: string; text: string }[] {
    return threadToConversation(this.thread);
  }

  /** Speaker notes derived from the model — the same source onSave persists, so
   *  the draft and an explicit save always agree on notes. */
  private compiledNotes(): string[] | undefined {
    return this.model.speakerNotes;
  }

  /** Title used when persisting — the model title, trimmed, with a non-empty
   *  fallback so the server's required-title contract is always satisfied. */
  private draftTitle(): string {
    return this.model.meta.title?.trim() || 'Untitled deck';
  }

  /** Debounced draft auto-save. Coalesces rapid edits; a no-op until a deck id
   *  exists (a from-scratch build that was never generated uses explicit Save to
   *  create it). Flushed on disconnectedCallback. */
  private schedulePersist() {
    if (!this.deckId) return;
    if (this.persistTimer) window.clearTimeout(this.persistTimer);
    this.persistTimer = window.setTimeout(() => {
      this.persistTimer = undefined;
      void this.persistDraft();
    }, 800);
  }

  /** Persist the current deck state to the existing draft. No-op when no deck id
   *  exists. Errors are soft-toasted, never thrown — auto-save must never block
   *  editing. */
  private async persistDraft() {
    if (!this.deckId) return;
    try {
      await api.updateDeck(this.deckId, {
        id: this.deckId,
        title: this.draftTitle(),
        subtitle: this.model.meta.subtitle?.trim() || undefined,
        author: this.model.meta.author?.trim() || undefined,
        html: renderDeck(this.model),
        speakerNotes: this.compiledNotes(),
        model: this.model,
        status: this.deckStatus ?? 'draft',
        conversation: this.threadToConversation(),
      });
    } catch {
      // Soft failure — the in-memory work is intact; do not interrupt editing.
      showToast('Draft auto-save failed — your work is still here.');
    }
  }

  // ── Title (shared across both modes) ────────────────────────────────────────
  private onTitleInput(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    this.apply(updateMeta(this.model, { title: value }));
  }

  // ── Generate mode ───────────────────────────────────────────────────────────
  private async onGenerate() {
    const prompt = this.prompt.trim();
    if (!prompt) {
      this.error = 'Enter a prompt to generate a deck.';
      return;
    }
    this.error = null;
    this.generating = true;
    try {
      const model = await api.generateDeck({
        prompt,
        slideCount: this.slideCount,
        tone: this.tone.trim() || undefined,
        quality: this.quality,
      });
      // Seed the shared model from the generated one. If the generated deck has
      // no title, keep the prompt's first words as a sensible fallback.
      this.model = model;
      if (!this.model.meta.title?.trim()) {
        this.model = updateMeta(this.model, { title: prompt.slice(0, 60) });
      }
      if (this.jsonOpen) this.jsonText = JSON.stringify(this.model, null, 2);
      this.compileNow();
      // Open the refine conversation: the prompt + a short ack. Once the thread
      // is non-empty the first-gen controls collapse into the chat composer.
      this.thread = [
        { role: 'user', text: prompt },
        { role: 'assistant', text: `Generated ${this.model.slides.length}-slide deck.` },
      ];
      // VISUAL SELF-CHECK: render, measure each slide's real auto-fit, and if any
      // are overpacked, silently ask the model to lighten exactly those (≤2
      // rounds). Updates this.model + the preview in place; not added to the
      // visible conversation. Runs before the draft is persisted so the saved
      // draft is the fitted one.
      await this.autoFitRevise();
      // INSTANT DRAFT: persist the freshly generated deck immediately so a
      // refresh resumes it. Failure is non-fatal — the in-memory deck stands and
      // generation is never blocked.
      try {
        const deck = await api.createDeck({
          title: this.draftTitle(),
          subtitle: this.model.meta.subtitle?.trim() || undefined,
          author: this.model.meta.author?.trim() || undefined,
          html: renderDeck(this.model),
          speakerNotes: this.compiledNotes(),
          model: this.model,
          status: 'draft',
          conversation: this.threadToConversation(),
        });
        this.deckId = deck.id;
        this.deckStatus = 'draft';
        // Resumable URL WITHOUT a router remount — replaceState keeps the page
        // mounted (no popstate) so generation state survives intact.
        history.replaceState({}, '', `/new/${encodeURIComponent(deck.id)}`);
      } catch {
        showToast('Draft could not be saved — your work is still here.');
      }
      showToast('Deck generated — refine it below or save.');
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Generation failed';
    } finally {
      this.generating = false;
    }
  }

  // ── Refine (chat-style follow-on) ─────────────────────────────────────────
  /** Send a follow-on instruction. Carries the prior conversation as `history`
   *  and the current deck as `currentModel` so the model edits in context. */
  private async onRefine() {
    const instruction = this.refineInput.trim();
    if (!instruction || this.generating) return;
    this.error = null;
    this.generating = true;
    // Show the user's turn immediately, and build the request from the thread
    // BEFORE the ack is appended (history = the conversation up to this turn).
    const turn: ThreadTurn = { role: 'user', text: instruction };
    this.thread = [...this.thread, turn];
    const req = buildRefineRequest({
      prompt: instruction,
      thread: this.thread,
      currentModel: this.model,
      quality: this.quality,
    });
    try {
      const model = await api.generateDeck(req);
      this.model = model;
      if (this.jsonOpen) this.jsonText = JSON.stringify(this.model, null, 2);
      this.compileNow();
      // SUCCESS — keep the user-then-assistant-ack pattern.
      this.thread = [
        ...this.thread,
        { role: 'assistant', text: `Updated · ${this.model.slides.length} slides.` },
      ];
      this.refineInput = '';
      this.scrollThreadToLatest();
      // Persist the new thread + model into the draft.
      this.schedulePersist();
    } catch (err) {
      // FAILURE — do NOT leave a dangling user turn. Answer it with an assistant
      // error turn, keep this.model unchanged, and re-enable the input so the
      // user can rephrase and retry (refineInput is preserved on purpose).
      this.error = err instanceof Error ? err.message : 'Refine failed';
      this.thread = [
        ...this.thread,
        { role: 'assistant', text: 'Could not apply that change — try rephrasing.' },
      ];
      this.scrollThreadToLatest();
      // Persist the conversation (incl. the error turn) so a refresh shows it.
      this.schedulePersist();
    } finally {
      this.generating = false;
    }
  }

  /** Scroll the refine thread to its latest turn after a render flush. */
  private scrollThreadToLatest() {
    void this.updateComplete.then(() => {
      const el = this.querySelector<HTMLElement>('.refine-thread');
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  /** Clear the conversation and reset to a blank deck so the user can run a
   *  fresh first generation. Returns the generate controls. */
  private startOver() {
    // Flush any pending auto-save for the OLD draft before detaching from it, so
    // its final state is kept (it survives in the list as a draft until deleted).
    if (this.persistTimer) {
      window.clearTimeout(this.persistTimer);
      this.persistTimer = undefined;
      void this.persistDraft();
    }
    this.thread = [];
    this.refineInput = '';
    this.prompt = '';
    this.error = null;
    this.model = emptyDeck();
    // Detach from the old draft: the next generation mints a fresh instant draft.
    this.deckId = null;
    this.deckStatus = null;
    this.deckIdParam = '';
    history.replaceState({}, '', '/new');
    if (this.jsonOpen) this.jsonText = JSON.stringify(this.model, null, 2);
    this.compileNow();
  }

  // ── Advanced JSON editor ────────────────────────────────────────────────────
  private toggleJson() {
    this.jsonOpen = !this.jsonOpen;
    if (this.jsonOpen) {
      this.jsonText = JSON.stringify(this.model, null, 2);
      this.jsonError = null;
    }
  }

  private onJsonInput(e: Event) {
    this.jsonText = (e.target as HTMLTextAreaElement).value;
  }

  private applyJson() {
    try {
      const parsed = JSON.parse(this.jsonText) as DeckModel;
      if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.slides) || !parsed.meta) {
        throw new Error('JSON must be a DeckModel with meta and slides[].');
      }
      this.jsonError = null;
      this.model = parsed;
      this.compileNow();
      this.schedulePersist();
      showToast('Model imported from JSON.');
    } catch (err) {
      this.jsonError = err instanceof Error ? err.message : 'Invalid JSON';
    }
  }

  // ── Insert-from-library picker (BUILD mode) ─────────────────────────────────
  /** The builtin reusable slides grouped by category, preserving catalogue
   *  order within each group. Computed on open; the catalogue is static. */
  private groupedTemplates(): Array<{ category: string; templates: SlideTemplate[] }> {
    const groups: Array<{ category: string; templates: SlideTemplate[] }> = [];
    const byCategory = new Map<string, SlideTemplate[]>();
    for (const tpl of getBuiltinTemplates()) {
      let bucket = byCategory.get(tpl.category);
      if (!bucket) {
        bucket = [];
        byCategory.set(tpl.category, bucket);
        groups.push({ category: tpl.category, templates: bucket });
      }
      bucket.push(tpl);
    }
    return groups;
  }

  private openLibrary() {
    this.libraryOpen = true;
  }

  private closeLibrary() {
    this.libraryOpen = false;
  }

  /** Insert a chosen reusable slide. Appended to the end of the deck (the
   *  builder has no per-slide focus), then recompile + toast + close. The
   *  helper deep-clones the catalogue slide, so the frozen builtin is never
   *  aliased into the editable model. */
  private insertTemplate(tpl: SlideTemplate) {
    this.apply(insertSlideTemplate(this.model, this.model.slides.length, tpl));
    this.libraryOpen = false;
    showToast(`Inserted "${tpl.name}".`);
  }

  // ── AI image generation (BUILD mode) ────────────────────────────────────────
  /**
   * Generate an image from the subject + style/theme/aspect controls, then
   * append it to the deck as a NEW content slide carrying a single `figure`
   * block. The IMAGE theme (light/dark/coral/navy) is a property of the
   * generated image only — it is deliberately decoupled from the deck's display
   * theme toggle (which themes the preview chrome, not the picture).
   *
   * Reuses the shared `apply()` mutation path so the new slide recompiles the
   * preview exactly like every other builder edit. Errors surface via
   * `this.error` + a toast; a 503 maps to a friendly "not configured" message.
   */
  private async onGenerateImage() {
    if (this.generatingImage) return;
    const subject = this.imageSubject.trim();
    if (!subject) {
      this.error = 'Describe the image you want to generate.';
      return;
    }
    this.error = null;
    this.generatingImage = true;
    try {
      const resp = await api.generateImage({
        subject,
        style: this.imageStyle,
        theme: this.imageTheme,
        aspect: this.imageAspect,
      });
      const n = String(this.model.slides.length + 1).padStart(2, '0');
      const slide: Slide = {
        variant: 'content',
        label: `${n} Image`,
        blocks: [
          {
            type: 'figure',
            image: { src: resp.src, alt: subject },
            caption: subject,
          },
        ],
      };
      // Append + recompile via the same mutation path the other edits use.
      // apply() already schedules a debounced draft persist.
      this.apply({ ...this.model, slides: [...this.model.slides, slide] });
      showToast('Image added as a new slide.');
    } catch (err) {
      if (err instanceof HttpError && err.status === 503) {
        this.error = 'Image generation is not configured.';
      } else if (err instanceof HttpError && err.status === 400) {
        this.error = 'Describe the image you want to generate.';
      } else {
        this.error = err instanceof Error ? err.message : 'Image generation failed';
      }
      showToast(`Image generation failed: ${this.error}`);
    } finally {
      this.generatingImage = false;
    }
  }

  // ── Save (shared) ───────────────────────────────────────────────────────────
  private async onSave() {
    const title = this.model.meta.title?.trim();
    if (!title) {
      this.error = 'A title is required before saving.';
      return;
    }
    this.error = null;
    this.saving = true;
    // Cancel any pending draft auto-save so it cannot race the promote/create
    // below (we are about to write the authoritative state).
    if (this.persistTimer) {
      window.clearTimeout(this.persistTimer);
      this.persistTimer = undefined;
    }
    try {
      // Always compile the CURRENT model so a half-typed debounce in flight
      // cannot save a stale html.
      const compiled = renderDeck(this.model);
      let id: string;
      if (this.deckId) {
        // PROMOTE the existing instant draft to a kept deck (status: saved).
        await api.updateDeck(this.deckId, {
          id: this.deckId,
          title,
          subtitle: this.model.meta.subtitle?.trim() || undefined,
          author: this.model.meta.author?.trim() || undefined,
          html: compiled,
          speakerNotes: this.compiledNotes(),
          model: this.model,
          status: 'saved',
          conversation: this.threadToConversation(),
        });
        this.deckStatus = 'saved';
        id = this.deckId;
      } else {
        // Pure from-scratch build that was never generated — create it directly
        // as a saved deck.
        const deck = await api.createDeck({
          title,
          subtitle: this.model.meta.subtitle?.trim() || undefined,
          author: this.model.meta.author?.trim() || undefined,
          html: compiled,
          speakerNotes: this.compiledNotes(),
          model: this.model,
          status: 'saved',
          conversation: this.threadToConversation(),
        });
        this.deckId = deck.id;
        this.deckStatus = 'saved';
        id = deck.id;
      }
      navigate(`/decks/${encodeURIComponent(id)}`);
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Save failed';
      showToast(`Save failed: ${this.error}`);
    } finally {
      this.saving = false;
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  render() {
    return html`
      <section class="page">
        <div class="page-head">
          <div class="titles">
            <h1>New deck</h1>
            <p class="page-sub">Generate with AI or build by hand · Workplace by Ricoh</p>
          </div>
          ${this.deckId && this.deckStatus === 'draft'
            ? html`<span
                class="draft-indicator"
                role="status"
                title="Saved automatically as a draft — refresh-safe"
                >● Draft saved</span
              >`
            : nothing}
        </div>

        <div class="create-layout">
          <div class="create-panel">${this.renderEditor()}</div>
          <div class="preview-pane">${this.renderPreview()}</div>
        </div>
      </section>
      ${this.renderMaximisedOverlay()}
      ${this.renderLibraryModal()}
    `;
  }

  private renderEditor() {
    return html`
      <div class="segmented" role="tablist" aria-label="Authoring mode">
        <button
          role="tab"
          class=${this.mode === 'generate' ? 'active' : ''}
          aria-selected=${this.mode === 'generate'}
          @click=${() => (this.mode = 'generate')}
        >Generate with AI</button>
        <button
          role="tab"
          class=${this.mode === 'build' ? 'active' : ''}
          aria-selected=${this.mode === 'build'}
          @click=${() => (this.mode = 'build')}
        >Build manually</button>
      </div>

      <div class="field">
        <label for="deck-title">Title</label>
        <input
          id="deck-title"
          class="input"
          type="text"
          .value=${this.model.meta.title ?? ''}
          @input=${this.onTitleInput}
          placeholder="Deck title"
        />
      </div>

      ${this.mode === 'generate' ? this.renderGenerate() : this.renderBuild()}

      ${this.error ? html`<p class="err" role="alert">${this.error}</p>` : nothing}

      <div class="save-row">
        <button
          class="btn-primary"
          type="button"
          ?disabled=${this.saving}
          @click=${this.onSave}
        >${this.saving
          ? 'Saving…'
          : this.deckStatus === 'draft'
            ? 'Keep deck'
            : 'Save deck'}</button>
        <button class="json-toggle" type="button" @click=${this.toggleJson}>
          ${this.jsonOpen ? 'Hide advanced JSON' : 'Advanced: edit JSON'}
        </button>
      </div>

      ${this.jsonOpen ? this.renderJson() : nothing}
    `;
  }

  private renderGenerate() {
    // Once a deck exists the first-gen controls collapse into a chat-style
    // refine thread + composer; until then, the original prompt + controls.
    return this.thread.length > 0 ? this.renderRefine() : this.renderFirstGen();
  }

  private renderFirstGen() {
    return html`
      <div class="field">
        <label for="gen-prompt">Prompt</label>
        <textarea
          id="gen-prompt"
          class="textarea"
          .value=${this.prompt}
          @input=${(e: Event) => (this.prompt = (e.target as HTMLTextAreaElement).value)}
          placeholder="Describe the deck you want — audience, topic, key points, takeaways."
        ></textarea>
      </div>
      <div class="field-row">
        <div class="field">
          <label for="gen-count">Slides</label>
          <input
            id="gen-count"
            class="input"
            type="number"
            min="1"
            max="20"
            .value=${String(this.slideCount)}
            @input=${(e: Event) => {
              const n = Number((e.target as HTMLInputElement).value);
              this.slideCount = Number.isFinite(n) ? Math.min(20, Math.max(1, Math.round(n))) : 8;
            }}
          />
        </div>
        <div class="field">
          <label for="gen-tone">Tone</label>
          <input
            id="gen-tone"
            class="input"
            type="text"
            .value=${this.tone}
            @input=${(e: Event) => (this.tone = (e.target as HTMLInputElement).value)}
            placeholder="e.g. executive, technical"
          />
        </div>
        <div class="field">
          <label for="gen-quality">Quality</label>
          <select
            id="gen-quality"
            class="select"
            .value=${this.quality}
            @change=${(e: Event) => (this.quality = (e.target as HTMLSelectElement).value as GenerateQuality)}
          >
            ${QUALITY_CHOICES.map(
              (q) => html`<option value=${q.value} ?selected=${this.quality === q.value}>${q.label}</option>`,
            )}
          </select>
        </div>
      </div>
      <button
        class="btn-primary"
        type="button"
        ?disabled=${this.generating}
        @click=${this.onGenerate}
      >${this.generating ? 'Generating…' : 'Generate'}</button>
      <p class="hint">
        The model is editable after generation — switch to "Build manually" to refine slides.
      </p>
    `;
  }

  /** The chat-style refine surface shown after the first generation: the
   *  conversation so far, then a follow-on composer + "Start over". */
  private renderRefine() {
    return html`
      <div class="refine-thread" aria-label="Refine conversation">
        ${this.thread.map(
          (turn) => html`
            <div class=${`refine-msg ${turn.role}`} data-role=${turn.role}>${turn.text}</div>
          `,
        )}
      </div>
      <div class="refine-composer">
        <div class="field" style="margin-bottom:0;">
          <label for="refine-input">Refine this deck</label>
          <textarea
            id="refine-input"
            class="textarea"
            .value=${this.refineInput}
            @input=${(e: Event) => (this.refineInput = (e.target as HTMLTextAreaElement).value)}
            placeholder=${'Refine this deck — e.g. "make slide 3 punchier" or "add a slide on pricing"'}
          ></textarea>
        </div>
        <div class="composer-actions">
          <button
            class="btn-primary"
            type="button"
            ?disabled=${this.generating || !this.refineInput.trim()}
            @click=${this.onRefine}
          >${this.generating ? 'Refining…' : 'Send'}</button>
          <button class="start-over" type="button" @click=${this.startOver}>Start over</button>
        </div>
      </div>
    `;
  }

  private renderBuild() {
    return html`
      <div class="field-row">
        <div class="field">
          <label for="meta-sub">Subtitle</label>
          <input
            id="meta-sub"
            class="input"
            type="text"
            .value=${this.model.meta.subtitle ?? ''}
            @input=${(e: Event) =>
              this.apply(updateMeta(this.model, { subtitle: (e.target as HTMLInputElement).value }))}
          />
        </div>
        <div class="field">
          <label for="meta-author">Author</label>
          <input
            id="meta-author"
            class="input"
            type="text"
            .value=${this.model.meta.author ?? ''}
            @input=${(e: Event) =>
              this.apply(updateMeta(this.model, { author: (e.target as HTMLInputElement).value }))}
          />
        </div>
      </div>

      ${this.model.slides.map((_, i) => this.renderSlideEditor(i))}

      <div class="builder-actions">
        <button class="btn-secondary" type="button" @click=${() => this.apply(addSlide(this.model))}>
          Add slide
        </button>
        <button class="btn-secondary" type="button" @click=${this.openLibrary}>
          Insert from library
        </button>
      </div>

      ${this.renderImageControl()}
    `;
  }

  /** BUILD-mode AI image control. Generates an on-theme image and appends it as
   *  a new figure slide. The "Image style/theme" selects describe the picture's
   *  look; they are independent of the deck's light/dark display toggle in the
   *  preview header (made explicit by the hint). */
  private renderImageControl(): TemplateResult {
    return html`
      <div class="image-gen">
        <div class="image-gen-head">
          <span class="image-gen-title">Generate image with AI</span>
          <span class="image-gen-sub">Adds a new image slide · Workplace by Ricoh</span>
        </div>
        <div class="field" style="margin-bottom:12px;">
          <label for="img-subject">Subject</label>
          <input
            id="img-subject"
            class="input"
            type="text"
            .value=${this.imageSubject}
            @input=${(e: Event) => (this.imageSubject = (e.target as HTMLInputElement).value)}
            placeholder="a calm modern office atrium, wide"
          />
        </div>
        <div class="field-row" style="margin-bottom:12px;">
          <div class="field" style="margin-bottom:0;">
            <label for="img-style">Image style</label>
            <select
              id="img-style"
              class="select"
              .value=${this.imageStyle}
              @change=${(e: Event) => (this.imageStyle = (e.target as HTMLSelectElement).value)}
            >
              ${IMAGE_STYLE_CHOICES.map(
                (s) => html`<option value=${s.value} ?selected=${this.imageStyle === s.value}>${s.label}</option>`,
              )}
            </select>
          </div>
          <div class="field" style="margin-bottom:0;">
            <label for="img-theme">Image theme</label>
            <select
              id="img-theme"
              class="select"
              .value=${this.imageTheme}
              @change=${(e: Event) => (this.imageTheme = (e.target as HTMLSelectElement).value)}
            >
              ${IMAGE_THEME_CHOICES.map(
                (t) => html`<option value=${t.value} ?selected=${this.imageTheme === t.value}>${t.label}</option>`,
              )}
            </select>
          </div>
          <div class="field" style="margin-bottom:0;">
            <label for="img-aspect">Aspect</label>
            <select
              id="img-aspect"
              class="select"
              .value=${this.imageAspect}
              @change=${(e: Event) => (this.imageAspect = (e.target as HTMLSelectElement).value)}
            >
              ${IMAGE_ASPECT_CHOICES.map(
                (a) => html`<option value=${a.value} ?selected=${this.imageAspect === a.value}>${a.label}</option>`,
              )}
            </select>
          </div>
        </div>
        <button
          class="btn-secondary"
          type="button"
          ?disabled=${this.generatingImage || !this.imageSubject.trim()}
          @click=${this.onGenerateImage}
        >${this.generatingImage ? 'Generating…' : 'Generate image'}</button>
        <p class="hint">
          Image style and theme describe the generated picture — separate from the deck's
          light/dark display toggle in the preview.
        </p>
      </div>
    `;
  }

  private renderSlideEditor(slideIndex: number): TemplateResult {
    const slide = this.model.slides[slideIndex];
    const slideCount = this.model.slides.length;
    return html`
      <div class="outline-slide">
        <div class="slide-head">
          <span class="idx">Slide ${String(slideIndex + 1).padStart(2, '0')}</span>
          <select
            class="select"
            style="width:auto;"
            aria-label="Slide ${slideIndex + 1} variant"
            @change=${(e: Event) =>
              this.apply(
                setSlideVariant(this.model, slideIndex, (e.target as HTMLSelectElement).value as SlideVariant),
              )}
          >
            ${SLIDE_VARIANTS.map(
              (v) => html`<option value=${v} ?selected=${(slide.variant ?? 'content') === v}>${v}</option>`,
            )}
          </select>
          <span class="spacer"></span>
          <button
            class="icon-btn"
            type="button"
            aria-label="Move slide up"
            title="Move up"
            ?disabled=${slideIndex === 0}
            @click=${() => this.apply(moveSlide(this.model, slideIndex, -1))}
          >↑</button>
          <button
            class="icon-btn"
            type="button"
            aria-label="Move slide down"
            title="Move down"
            ?disabled=${slideIndex === slideCount - 1}
            @click=${() => this.apply(moveSlide(this.model, slideIndex, 1))}
          >↓</button>
          <button
            class="icon-btn"
            type="button"
            aria-label="Delete slide"
            title="Delete slide"
            ?disabled=${slideCount <= 1}
            @click=${() => this.apply(removeSlide(this.model, slideIndex))}
          >✕</button>
        </div>

        ${slide.blocks.map((b, j) => this.renderBlockEditor(slideIndex, j, b))}

        <div class="add-block-row">
          <select class="select" aria-label="New block type" id=${`add-block-${slideIndex}`}>
            ${BLOCK_TYPES.map((t) => html`<option value=${t}>${t}</option>`)}
          </select>
          <button
            class="btn-secondary"
            style="height:34px;"
            type="button"
            @click=${(e: Event) => {
              const sel = (e.target as HTMLElement)
                .closest('.add-block-row')
                ?.querySelector('select') as HTMLSelectElement | null;
              const type = (sel?.value ?? 'paragraph') as BlockType;
              this.apply(addBlock(this.model, slideIndex, type));
            }}
          >Add block</button>
        </div>
      </div>
    `;
  }

  private renderBlockEditor(slideIndex: number, blockIndex: number, block: Block): TemplateResult {
    const slide = this.model.slides[slideIndex];
    const head = html`
      <div class="block-head">
        <span class="block-type">${block.type}</span>
        <button
          class="icon-btn"
          type="button"
          aria-label="Move block up"
          title="Move up"
          ?disabled=${blockIndex === 0}
          @click=${() => this.apply(moveBlock(this.model, slideIndex, blockIndex, -1))}
        >↑</button>
        <button
          class="icon-btn"
          type="button"
          aria-label="Move block down"
          title="Move down"
          ?disabled=${blockIndex === slide.blocks.length - 1}
          @click=${() => this.apply(moveBlock(this.model, slideIndex, blockIndex, 1))}
        >↓</button>
        <button
          class="icon-btn"
          type="button"
          aria-label="Delete block"
          title="Delete block"
          @click=${() => this.apply(removeBlock(this.model, slideIndex, blockIndex))}
        >✕</button>
      </div>
    `;
    return html`<div class="outline-block">${head}${this.renderBlockFields(slideIndex, blockIndex, block)}</div>`;
  }

  /** The per-type field set. Covers the required builder blocks (title,
   *  paragraph, bullets) plus cards + stat-row with simple repeatable fields;
   *  the remaining block types are editable via the advanced JSON pane. */
  private renderBlockFields(slideIndex: number, blockIndex: number, block: Block): TemplateResult {
    const patch = (p: Partial<Block>) => this.apply(updateBlock(this.model, slideIndex, blockIndex, p));

    switch (block.type) {
      case 'title':
        return html`
          <input
            class="input"
            type="text"
            placeholder="Eyebrow (optional)"
            .value=${block.eyebrow ?? ''}
            @input=${(e: Event) => patch({ eyebrow: (e.target as HTMLInputElement).value } as Partial<Block>)}
            style="margin-bottom:8px;"
          />
          <input
            class="input"
            type="text"
            placeholder="Title"
            .value=${block.title}
            @input=${(e: Event) => patch({ title: (e.target as HTMLInputElement).value } as Partial<Block>)}
            style="margin-bottom:8px;"
          />
          <input
            class="input"
            type="text"
            placeholder="Lede (optional)"
            .value=${block.lede ?? ''}
            @input=${(e: Event) => patch({ lede: (e.target as HTMLInputElement).value } as Partial<Block>)}
          />
        `;

      case 'heading':
        return html`
          <div class="field-row">
            <select
              class="select"
              aria-label="Heading level"
              @change=${(e: Event) =>
                patch({ level: Number((e.target as HTMLSelectElement).value) as 1 | 2 | 3 } as Partial<Block>)}
            >
              ${[1, 2, 3].map(
                (l) => html`<option value=${l} ?selected=${block.level === l}>H${l}</option>`,
              )}
            </select>
            <input
              class="input"
              type="text"
              placeholder="Heading text"
              .value=${block.text}
              @input=${(e: Event) => patch({ text: (e.target as HTMLInputElement).value } as Partial<Block>)}
            />
          </div>
        `;

      case 'paragraph':
      case 'lede':
        return html`
          <textarea
            class="textarea"
            placeholder=${block.type === 'lede' ? 'Lede text' : 'Paragraph text'}
            .value=${block.text}
            @input=${(e: Event) => patch({ text: (e.target as HTMLTextAreaElement).value } as Partial<Block>)}
          ></textarea>
        `;

      case 'bullets':
        return html`
          <label
            style="display:flex;align-items:center;gap:6px;font-size:0.82rem;color:var(--text-secondary);margin-bottom:8px;"
          >
            <input
              type="checkbox"
              .checked=${!!block.ordered}
              @change=${(e: Event) => patch({ ordered: (e.target as HTMLInputElement).checked } as Partial<Block>)}
            />
            Numbered list
          </label>
          <textarea
            class="textarea"
            placeholder="One item per line"
            .value=${block.items.join('\n')}
            @input=${(e: Event) =>
              patch({
                items: (e.target as HTMLTextAreaElement).value.split('\n').filter((s) => s.length > 0),
              } as Partial<Block>)}
          ></textarea>
        `;

      case 'quote':
        return html`
          <textarea
            class="textarea"
            placeholder="Quote text"
            .value=${block.text}
            @input=${(e: Event) => patch({ text: (e.target as HTMLTextAreaElement).value } as Partial<Block>)}
            style="margin-bottom:8px;"
          ></textarea>
          <input
            class="input"
            type="text"
            placeholder="Attribution (optional)"
            .value=${block.attribution ?? ''}
            @input=${(e: Event) => patch({ attribution: (e.target as HTMLInputElement).value } as Partial<Block>)}
          />
        `;

      case 'cards':
        return html`
          <div class="field-row" style="margin-bottom:8px;">
            <select
              class="select"
              aria-label="Card columns"
              @change=${(e: Event) =>
                patch({ columns: Number((e.target as HTMLSelectElement).value) as 2 | 3 } as Partial<Block>)}
            >
              ${[2, 3].map(
                (c) => html`<option value=${c} ?selected=${(block.columns ?? 2) === c}>${c} columns</option>`,
              )}
            </select>
          </div>
          <textarea
            class="textarea"
            placeholder="One card per line, as 'Title | Description'"
            .value=${block.cards.map((c) => `${c.title}${c.desc ? ` | ${c.desc}` : ''}`).join('\n')}
            @input=${(e: Event) =>
              patch({
                cards: (e.target as HTMLTextAreaElement).value
                  .split('\n')
                  .filter((s) => s.trim().length > 0)
                  .map((line) => {
                    const [title, desc] = line.split('|').map((p) => p.trim());
                    return desc ? { title, desc } : { title };
                  }),
              } as Partial<Block>)}
          ></textarea>
        `;

      case 'stat-row':
        return html`
          <textarea
            class="textarea"
            placeholder="One stat per line, as 'Number | Label'"
            .value=${block.stats.map((s) => `${s.n} | ${s.label}`).join('\n')}
            @input=${(e: Event) =>
              patch({
                stats: (e.target as HTMLTextAreaElement).value
                  .split('\n')
                  .filter((s) => s.trim().length > 0)
                  .map((line) => {
                    const [n, label] = line.split('|').map((p) => p.trim());
                    return { n: n ?? '', label: label ?? '' };
                  }),
              } as Partial<Block>)}
          ></textarea>
        `;

      // table / kv / rule / raw-html are edited via the advanced JSON pane in
      // v1 — they are less common and benefit from full structural control.
      default:
        return html`<p class="hint">Edit "${block.type}" blocks via the advanced JSON pane below.</p>`;
    }
  }

  private renderJson() {
    return html`
      <textarea
        class="json-area"
        aria-label="DeckModel JSON"
        .value=${this.jsonText}
        @input=${this.onJsonInput}
      ></textarea>
      ${this.jsonError ? html`<p class="err" role="alert">${this.jsonError}</p>` : nothing}
      <div class="builder-actions">
        <button class="btn-secondary" type="button" @click=${this.applyJson}>Apply JSON</button>
      </div>
    `;
  }

  private renderPreview() {
    return html`
      <div class="preview-head">
        <span class="label">Live preview</span>
        <button
          class="preview-maximise"
          type="button"
          aria-label="Maximise preview"
          title="Maximise preview"
          @click=${() => (this.previewMaximised = true)}
        >⤢ Maximise</button>
      </div>
      <div class="preview-frame">
        <iframe
          class="preview"
          title="Deck preview"
          srcdoc=${this.compiledHtml}
          sandbox="allow-scripts allow-popups allow-forms allow-modals"
          allow="fullscreen"
          @load=${this.onIframeLoad}
        ></iframe>
      </div>
    `;
  }

  /** The full-viewport DRAFT preview overlay. Keyed off previewMaximised so the
   *  second iframe mounts only while open. The DRAFT badge is the key signal:
   *  the user is in generation/draft mode, NOT viewing a saved deck. The iframe
   *  reuses the inline preview's srcdoc + sandbox + allow + @load exactly. */
  private renderMaximisedOverlay() {
    if (!this.previewMaximised) return nothing;
    const title = this.model.meta.title?.trim() || 'Untitled deck';
    return html`
      <div class="preview-overlay" role="dialog" aria-modal="true" aria-label="Draft deck preview">
        <div class="preview-overlay-bar">
          <span class="draft-badge">
            DRAFT PREVIEW
            <span class="draft-badge-sub">Generation mode</span>
          </span>
          <span class="preview-overlay-title" title=${title}>${title}</span>
          <button
            class="btn-secondary"
            type="button"
            aria-label="Close preview"
            @click=${() => (this.previewMaximised = false)}
          >Close</button>
        </div>
        <iframe
          class="preview-overlay-frame"
          title="Draft deck preview"
          srcdoc=${this.compiledHtml}
          sandbox="allow-scripts allow-popups allow-forms allow-modals"
          allow="fullscreen"
          @load=${this.onIframeLoad}
        ></iframe>
      </div>
    `;
  }

  /** The reusable-slide picker modal (BUILD mode). Lists the builtin templates
   *  grouped by category, each row showing name + description (+ a "Workplace
   *  by Ricoh" badge for product-canon slides) and a tiny live preview
   *  thumbnail. Selecting a row inserts the slide into the deck. Mounted only
   *  while open so the thumbnail iframes are not built until needed. */
  private renderLibraryModal() {
    if (!this.libraryOpen) return nothing;
    const groups = this.groupedTemplates();
    return html`
      <div
        class="library-overlay"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.closeLibrary();
        }}
      >
        <div class="library-modal" role="dialog" aria-modal="true" aria-label="Insert from library">
          <div class="library-bar">
            <span class="library-title">Insert from library</span>
            <span class="library-sub">Reusable slides · Workplace by Ricoh</span>
            <span class="spacer"></span>
            <button
              class="btn-secondary"
              type="button"
              aria-label="Close library"
              @click=${this.closeLibrary}
            >Close</button>
          </div>
          <div class="library-body">
            ${groups.map(
              (group) => html`
                <div class="library-group">
                  <h3 class="library-group-title">${group.category}</h3>
                  <div class="library-grid">
                    ${group.templates.map((tpl) => this.renderTemplateRow(tpl))}
                  </div>
                </div>
              `,
            )}
          </div>
        </div>
      </div>
    `;
  }

  /** A single template card: live thumbnail + name + description (+ badge).
   *  Clicking anywhere on the card inserts the slide. All template text is
   *  rendered through Lit text interpolation (escaped) — never unsafeHTML. */
  private renderTemplateRow(tpl: SlideTemplate): TemplateResult {
    // Compile a one-slide deck for the preview, using the CURRENT deck meta so
    // theme/accent match. renderDeck is total over a valid model; on the off
    // chance of a malformed catalogue slide, fall back to an empty thumbnail.
    let srcdoc = '';
    try {
      srcdoc = renderDeck({ meta: this.model.meta, slides: [tpl.slide] });
    } catch {
      srcdoc = '';
    }
    return html`
      <button
        class="tpl-card"
        type="button"
        data-tpl-id=${tpl.id}
        @click=${() => this.insertTemplate(tpl)}
      >
        <div class="tpl-thumb">
          ${srcdoc
            ? html`
                <iframe
                  class="tpl-thumb-frame"
                  title=${`Preview of ${tpl.name}`}
                  srcdoc=${srcdoc}
                  sandbox="allow-scripts allow-popups allow-forms allow-modals"
                  allow="fullscreen"
                  @load=${this.onThumbLoad}
                ></iframe>
              `
            : nothing}
        </div>
        <div class="tpl-meta">
          <span class="tpl-name">
            ${tpl.name}
            ${tpl.describesWorkplace
              ? html`<span class="tpl-badge">Workplace by Ricoh</span>`
              : nothing}
          </span>
          <span class="tpl-desc">${tpl.description}</span>
        </div>
      </button>
    `;
  }

  /** A template thumbnail finished loading — theme it AND scale the 1920x1080
   *  iframe down to fit its 16/9 box (same recipe as the analytics thumbnails:
   *  transform: scale(boxWidth / 1920)). */
  private onThumbLoad = (e: Event) => {
    const iframe = e.target as HTMLIFrameElement;
    this.postThemeToFrame(iframe, this.currentTheme());
    this.fitThumb(iframe);
  };

  private fitThumb(iframe: HTMLIFrameElement) {
    const box = iframe.parentElement;
    if (!box) return;
    const width = box.clientWidth;
    if (!width) return;
    iframe.style.transform = `scale(${width / 1920})`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'create-page': CreatePage;
  }
}
